// DEM-2129 — Loops C · Detalhe de um Loop (/loops/:id).
//
// Configuração completa + próximas ocorrências (preview client-side/backend) +
// histórico de runs (status, duração, custo, erro, link pro dispatch — degradando
// com '—' quando ausente) + ações editar/pausar/retomar/excluir.

import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Pause, Play, Pencil, Trash2 } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { StatusBadge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { Alert } from '@/components/ui/Alert';
import { useToast } from '@/components/ui/Toast';
import { ConfirmActionDialog } from '@/components/ConfirmActionDialog';
import { api } from '@/lib/api';
import type { AgentListItem, LoopOutputChannel } from '@/lib/api';
import type { LoopDetail } from '@/lib/types/loops';
import { fmtDateTime, formatCostStrict } from '@/lib/format';
import { resolvePreview } from '@/lib/loops-rrule';
import { LoopWizard } from '@/components/loops/LoopWizard';
import {
  loopScheduleHuman,
  loopStatusBadge,
  loopStatusLabel,
  loopAgentName,
  loopChannelLabel,
  runStatusBadge,
  runStatusLabel,
  runDuration,
  dispatchLink,
  orDash,
} from '@/components/loops/loops-ui';

export function LoopDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [loop, setLoop] = useState<LoopDetail | null>(null);
  // Fallback client-side (janela até o restart): resolve canal/agente quando o
  // backend vivo ainda não manda as projeções output_channel/agent_name.
  const [channels, setChannels] = useState<LoopOutputChannel[]>([]);
  const [agents, setAgents] = useState<AgentListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nextOccurrences, setNextOccurrences] = useState<Date[]>([]);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const [detail, chs, ags] = await Promise.all([
        api.getLoop(id),
        api.listLoopChannels().catch(() => [] as LoopOutputChannel[]),
        api.listAgents().catch(() => [] as AgentListItem[]),
      ]);
      setLoop(detail);
      setChannels(chs);
      setAgents(ags);
      // próximas ocorrências: backend /preview com fallback client-side.
      const result = await resolvePreview(
        detail.schedule_rrule,
        detail.start_at ? new Date(detail.start_at) : null,
        (payload) => api.previewLoopOccurrences(payload),
      );
      setNextOccurrences(result.occurrences);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao carregar loop');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const togglePause = useCallback(async () => {
    if (!loop) return;
    setBusy(true);
    try {
      const updated =
        loop.status === 'paused' ? await api.resumeLoop(loop.id) : await api.pauseLoop(loop.id);
      setLoop((prev) => (prev ? { ...prev, ...updated } : prev));
    } catch (err) {
      toast({ message: err instanceof Error ? err.message : 'Falha ao alterar status', type: 'error' });
    } finally {
      setBusy(false);
    }
  }, [loop, toast]);

  const handleDelete = useCallback(async () => {
    if (!loop) return;
    await api.deleteLoop(loop.id);
    toast({ message: 'Loop excluído', type: 'success' });
    navigate('/loops');
  }, [loop, navigate, toast]);

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (error || !loop) {
    return (
      <div className="space-y-4">
        <Link to="/loops" className="inline-flex items-center gap-1 text-sm text-raios-accent-text">
          <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Voltar
        </Link>
        <Alert variant="destructive">{error ?? 'Loop não encontrado'}</Alert>
      </div>
    );
  }

  const isActionable = loop.status !== 'archived' && loop.status !== 'completed';
  const channelById = new Map(channels.map((c) => [c.id, { id: c.id, label: c.label, detail: c.detail ?? null }]));
  const agentNameBySlug = new Map(agents.filter((a) => a.name).map((a) => [a.slug, a.name]));

  return (
    <div className="space-y-6">
      <Link to="/loops" className="inline-flex items-center gap-1 text-sm text-raios-accent-text">
        <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Loops
      </Link>

      <PageHeader
        title={loop.name}
        description={`${loopAgentName(loop, agentNameBySlug)} · ${loopChannelLabel(loop, channelById).label}`}
        actions={
          <div className="flex items-center gap-2">
            {isActionable && (
              <Button variant="outline" size="sm" loading={busy} onClick={togglePause}>
                {loop.status === 'paused' ? (
                  <>
                    <Play className="h-3.5 w-3.5 mr-1" aria-hidden="true" /> Retomar
                  </>
                ) : (
                  <>
                    <Pause className="h-3.5 w-3.5 mr-1" aria-hidden="true" /> Pausar
                  </>
                )}
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
              <Pencil className="h-3.5 w-3.5 mr-1" aria-hidden="true" /> Editar
            </Button>
            <Button variant="destructive" size="sm" onClick={() => setDeleteOpen(true)}>
              <Trash2 className="h-3.5 w-3.5 mr-1" aria-hidden="true" /> Excluir
            </Button>
          </div>
        }
      />

      {/* Configuração */}
      <Card className="p-4">
        <h2 className="text-sm font-semibold text-raios-text mb-3">Configuração</h2>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <ConfigRow label="Agente">
            <span data-testid="loop-detail-agent">
              {loopAgentName(loop, agentNameBySlug)}
              {loop.agent_name && loop.agent_name !== loop.target_slug && (
                <span className="ml-1.5 text-xs text-raios-muted">({loop.target_slug})</span>
              )}
            </span>
          </ConfigRow>
          <ConfigRow label="Canal de saída">
            <span data-testid="loop-detail-channel">{loopChannelLabel(loop, channelById).label}</span>
          </ConfigRow>
          <ConfigRow label="Status">
            <StatusBadge status={loopStatusBadge(loop.status)} label={loopStatusLabel(loop.status)} />
          </ConfigRow>
          <ConfigRow label="Periodicidade">{loopScheduleHuman(loop)}</ConfigRow>
          <ConfigRow label="RRULE" mono>{loop.schedule_rrule}</ConfigRow>
          <ConfigRow label="Próxima execução">
            {loop.status === 'paused'
              ? 'Pausado — recalculada ao retomar'
              : orDash(loop.next_run_at ? fmtDateTime(loop.next_run_at) : null)}
          </ConfigRow>
          <ConfigRow label="Início">{orDash(loop.start_at ? fmtDateTime(loop.start_at) : 'imediato')}</ConfigRow>
          <ConfigRow label="Encerramento">{orDash(loop.end_at ? fmtDateTime(loop.end_at) : 'sem fim')}</ConfigRow>
          <ConfigRow label="Catch-up">{loop.catch_up_policy === 'run_once' ? 'Executar perdidos' : 'Pular'}</ConfigRow>
          <ConfigRow label="Orçamento diário">
            {loop.budget_daily_usd === null || loop.budget_daily_usd === undefined || loop.budget_daily_usd < 0
              ? 'Ilimitado'
              : formatCostStrict(loop.budget_daily_usd)}
          </ConfigRow>
        </dl>
      </Card>

      {/* Próximas ocorrências */}
      <Card className="p-4">
        <h2 className="text-sm font-semibold text-raios-text mb-3">Próximas ocorrências</h2>
        {nextOccurrences.length === 0 ? (
          <p className="text-sm text-raios-muted">Sem ocorrências futuras.</p>
        ) : (
          <ul className="space-y-1 text-sm text-raios-text-secondary">
            {nextOccurrences.map((d, i) => (
              <li key={i}>{fmtDateTime(d)}</li>
            ))}
          </ul>
        )}
      </Card>

      {/* Histórico de runs */}
      <Card className="p-4">
        <h2 className="text-sm font-semibold text-raios-text mb-3">Histórico de execuções</h2>
        {loop.runs.length === 0 ? (
          <p className="text-sm text-raios-muted">Nenhuma execução ainda.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-raios-muted border-b border-raios-border">
                  <th className="py-2 pr-4 font-medium">Status</th>
                  <th className="py-2 pr-4 font-medium">Agendada</th>
                  <th className="py-2 pr-4 font-medium">Duração</th>
                  <th className="py-2 pr-4 font-medium">Custo</th>
                  <th className="py-2 pr-4 font-medium">Erro</th>
                  <th className="py-2 pr-4 font-medium">Dispatch</th>
                </tr>
              </thead>
              <tbody>
                {loop.runs.map((run) => {
                  const dl = dispatchLink(run.dispatch_ref);
                  return (
                    <tr key={run.id} className="border-b border-raios-border/50" data-testid="loop-run-row">
                      <td className="py-2 pr-4">
                        <StatusBadge status={runStatusBadge(run.status)} label={runStatusLabel(run.status)} />
                      </td>
                      <td className="py-2 pr-4 text-raios-text-secondary">{fmtDateTime(run.scheduled_for)}</td>
                      <td className="py-2 pr-4 text-raios-text-secondary">
                        {runDuration(run.started_at, run.finished_at)}
                      </td>
                      <td className="py-2 pr-4 text-raios-text-secondary">
                        {run.cost_usd !== null && run.cost_usd !== undefined
                          ? formatCostStrict(run.cost_usd)
                          : '—'}
                      </td>
                      <td className="py-2 pr-4 text-raios-danger-text max-w-[240px] truncate" title={run.error ?? ''}>
                        {orDash(run.error)}
                      </td>
                      <td className="py-2 pr-4">
                        {dl.href ? (
                          <Link to={dl.href} className="text-raios-accent-text hover:underline">
                            {dl.label}
                          </Link>
                        ) : (
                          <span className="text-raios-muted">{dl.label}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <LoopWizard
        open={editOpen}
        onOpenChange={setEditOpen}
        loop={loop}
        onSaved={(updated) => {
          setEditOpen(false);
          setLoop((prev) => (prev ? { ...prev, ...updated } : prev));
          void load();
        }}
      />

      <ConfirmActionDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Excluir loop?"
        description="Esta ação arquiva o loop e interrompe as execuções futuras."
        confirmLabel="Excluir"
        confirmVariant="destructive"
        confirmIcon={Trash2}
        onConfirm={handleDelete}
      >
        <p className="text-sm text-raios-text-secondary">
          Loop: <span className="font-medium text-raios-text">{loop.name}</span>
        </p>
      </ConfirmActionDialog>
    </div>
  );
}

function ConfigRow({
  label,
  children,
  mono,
}: {
  label: string;
  children: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="flex flex-col">
      <dt className="text-xs text-raios-muted">{label}</dt>
      <dd className={mono ? 'font-mono text-xs text-raios-text-secondary break-all' : 'text-raios-text'}>
        {children}
      </dd>
    </div>
  );
}
